import { useEffect, useState, useMemo, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { ModelList } from './components/models/model-list'
import { FlowChart } from './components/flow/flow-chart'
import { Sheet, SheetTrigger, SheetContent } from './components/ui/sheet'
import { Button } from './components/ui/button'
import { Settings, LayoutGrid } from 'lucide-react'
import { API_URL } from './config'
import { useWebSocket } from './hooks/use-websocket'
import { SessionManager } from './components/shared/session-manager'
import { Panel } from './components/ui/panel'
import graphchatLogo from './assets/graphchat-logo.svg'
import { ReactFlowProvider } from 'reactflow'
import type { Message } from './types/message'
import type { DataNodeUpdateMessage } from '../shared/ws-messages'
import { SignInPage } from './pages/SignInPage'

// Helper function to find a node by ID in the tree
const findNodeById = (root: Message, id: string): Message | null => {
  if (root.id === id) return root;
  for (const child of root.children) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
};

export function App() {
  const [rootNode, setRootNode] = useState<Message | null>(null)
  const [allNodes, setAllNodes] = useState<Record<string, Message>>({})
  const [currentNode, setCurrentNode] = useState<string | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isMessageStreaming, setIsMessageStreaming] = useState(false)
  const [models, setModels] = useState<string[]>([])
  const [activeModels, setActiveModels] = useState<Set<string>>(new Set())
  const savedSessionId = localStorage.getItem('session_id');
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const [clerkToken, setClerkToken] = useState<string | null>(null)
  const sessionId = useMemo(() => {
    if (!savedSessionId) {
      const newId = crypto.randomUUID();
      localStorage.setItem('session_id', newId);
      return newId;
    }
    return savedSessionId;
  }, [savedSessionId]);

  useEffect(() => {
    let active = true

    if (!isLoaded || !isSignedIn) {
      setClerkToken(null)
      return
    }

    const refreshToken = async () => {
      try {
        const token = await getToken()
        if (active) {
          setClerkToken(token || null)
        }
      } catch (error) {
        console.error('Failed to load Clerk token', error)
        if (active) {
          setClerkToken(null)
        }
      }
    }

    refreshToken()
    const interval = window.setInterval(refreshToken, 4 * 60 * 1000)

    return () => {
      active = false
      window.clearInterval(interval)
    }
  }, [isLoaded, isSignedIn, getToken])

  // Create WebSocket URL
  const wsUrl = useMemo(() => {
    if (!clerkToken) {
      return null
    }
    if (!API_URL) {
      // For production, use relative WebSocket URL
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}/ws/${sessionId}`;
    }
    return `${API_URL.replace('http', 'ws')}/ws/${sessionId}`;
  }, [sessionId, clerkToken])

  // Connect to WebSocket with no reconnection in dev
  const { sendMessage, lastMessage, readyState, connectionError } = useWebSocket(wsUrl, {
    reconnectAttempts: import.meta.env.DEV ? 0 : 5,
    reconnectInterval: 3000,
    healthCheckInterval: 30000,
    authToken: clerkToken
  })

  // Show connection error if any
  useEffect(() => {
    if (connectionError) {
      console.error('WebSocket connection error:', connectionError)
      // You can add UI notification here if needed
    }
  }, [connectionError])

  // Initialize session
  useEffect(() => {
    if (sessionId) {
      console.log('Initializing session:', sessionId)
      localStorage.setItem('session_id', sessionId)
    }
  }, [sessionId])

  // Request models when connected
  useEffect(() => {
    if (readyState === WebSocket.OPEN) {
      console.log('Requesting models list')
      try {
        // Add a small delay to ensure connection is fully ready
        const timeoutId = setTimeout(() => {
          if (readyState === WebSocket.OPEN) {
            sendMessage({ type: 'get_models' })
          }
        }, 100)
        return () => clearTimeout(timeoutId)
      } catch (error) {
        console.error('Failed to request models:', error)
      }
    }
  }, [readyState, sendMessage])

  // Process WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data = lastMessage.data;
      
      if (data.type === 'stream') {
        // Handle streaming message
        if (!data.node_id) {
          console.error('Invalid stream data:', data);
          return;
        }

        setRootNode(prevRoot => {
          if (!prevRoot) return prevRoot;

          // Helper function to update node content
          const updateNodeContent = (node: Message): Message => {
            if (node.id === data.node_id) {
              // Append new content instead of replacing
              const updatedContent = data.content 
                ? (node.content || '') + data.content
                : node.content || '';
                
              return {
                ...node,
                content: updatedContent,
                metadata: {
                  ...node.metadata,
                  streaming: !data.done,
                  error: data.error
                }
              };
            }

            return {
              ...node,
              children: node.children.map(updateNodeContent)
            };
          };

          const updatedRoot = updateNodeContent(prevRoot);
          return updatedRoot;
        });

        if (data.done) {
          setIsMessageStreaming(false);
        }
      } else if (data.type === 'full_state') {
        console.log('Received full state:', {
          rootId: data.rootId,
          nodeCount: Object.keys(data.nodes).length,
          currentNode: data.currentNode,
          nodes: Object.entries(data.nodes).map(([id, node]) => ({
            id,
            metadata: node.metadata
          }))
        });

        // Store all nodes in state
        setAllNodes(data.nodes as unknown as Record<string, Message>);

        // Build node tree
        const buildTree = (nodeId: string): Message | null => {
          const node = data.nodes[nodeId];
          if (!node) return null;

          return {
            id: node.id,
            content: node.content,
            role: node.role,
            model: node.model,
            metadata: node.metadata || {},  // Ensure metadata is never undefined
            children: node.children.map(childId => {
              const child = buildTree(childId);
              return child || { id: childId, content: '', role: '', model: null, metadata: {}, children: [], parent_id: null };
            }),
            parent_id: node.parent_id
          };
        };

        const rootNode = buildTree(data.rootId);
        
        if (rootNode) {
          // Use a state updater to ensure we have the latest state
          setRootNode(prevNode => {
            // Skip update if the tree structure hasn't changed
            if (prevNode && prevNode.id === rootNode.id && 
                JSON.stringify(prevNode) === JSON.stringify(rootNode)) {
              return prevNode;
            }
            
            // Preserve existing metadata for nodes
            const preserveMetadata = (node: Message): Message => {
              const existingNode = prevNode ? findNodeById(prevNode, node.id) : null;
              return {
                ...node,
                metadata: {
                  ...(existingNode?.metadata || {}),  // Keep existing metadata
                  ...node.metadata  // Add new metadata
                },
                children: node.children.map(preserveMetadata)
              };
            };

            return preserveMetadata(rootNode);
          });

          setCurrentNode(prevNode => {
            // Only update if the current node exists in the new tree
            if (data.currentNode && data.nodes[data.currentNode]) {
              return data.currentNode;
            }
            return prevNode;
          });
        } else {
          console.error('Failed to build valid node tree from state');
        }

        setIsInitialLoading(false);
      } else if (data.type === 'data_node_update') {
        const updateData = data as DataNodeUpdateMessage;
        console.log('Received data node update:', {
          nodeId: updateData.node_id,
          status: updateData.status,
          metadata: updateData.metadata,
          error: updateData.error,
          workflowRunId: updateData.workflowRunId
        });
        
        setRootNode(prevRoot => {
          if (!prevRoot) return prevRoot;

          // Helper function to update node metadata
          const updateNodeMetadata = (node: Message): Message => {
            if (node.id === updateData.node_id) {
              // Check if metadata actually changed to prevent unnecessary updates
              const newMetadata = {
                ...(node.metadata || {}),
                ...(updateData.metadata || {}),
                status: updateData.status,
                error: updateData.error
              };

              // Add workflowRunId if present
              if (updateData.workflowRunId) {
                newMetadata.workflowRunId = updateData.workflowRunId;
              }

              // Only update if metadata actually changed
              if (JSON.stringify(node.metadata) === JSON.stringify(newMetadata)) {
                return node;
              }

              console.log('Updating node metadata:', {
                nodeId: node.id,
                oldMetadata: node.metadata,
                newMetadata,
                output: newMetadata.output?.substring(0, 100) + '...',
                workflowRunId: newMetadata.workflowRunId
              });

              return {
                ...node,
                metadata: newMetadata
              };
            }

            // Only update children if they contain the target node
            const updatedChildren = node.children.map(child => {
              const foundNode = findNodeById(child, updateData.node_id);
              return foundNode ? updateNodeMetadata(child) : child;
            });

            // Only create new node reference if children changed
            if (updatedChildren === node.children) {
              return node;
            }

            return {
              ...node,
              children: updatedChildren
            };
          };

          const updatedRoot = updateNodeMetadata(prevRoot);
          return updatedRoot === prevRoot ? prevRoot : updatedRoot;
        });
      } else if (data.type === 'models') {
        console.log('Received models update:', data);
        if (Array.isArray(data.models)) {
          setModels(data.models);
          setActiveModels(new Set(data.activeModels || []));
        } else {
          console.error('Invalid models data received:', data);
        }
      }
    } catch (error) {
      console.error('Error processing WebSocket message:', error);
      setIsMessageStreaming(false);
      setIsInitialLoading(false);
    }
  }, [lastMessage]);

  // Message handlers

  const handleSendMessage = useCallback((content: string | { type: string, path?: string[], data?: any, [key: string]: any }) => {
    if (readyState !== WebSocket.OPEN) return;
    
    // Only show streaming indicator for chat messages
    if (typeof content === 'string') {
      // Handle chat messages
      if (!content.trim()) return;
      
      setIsMessageStreaming(true);
      sendMessage({
        type: 'chat',
        message: content,
        models: Array.from(activeModels),
        path: currentNode ? [currentNode] : undefined
      });
    } else {
      // Handle command messages without showing streaming indicator
      sendMessage({
        ...content,
        models: Array.from(activeModels),
        path: content.path || (currentNode ? [currentNode] : undefined)
      } as any);
    }
  }, [sendMessage, activeModels, currentNode, readyState])

  const handleSelectResponse = useCallback((nodeId: string) => {
    setCurrentNode(nodeId)
    if (readyState === WebSocket.OPEN) {
      sendMessage({
        type: 'select',
        node_id: nodeId
      })
    }
  }, [sendMessage, readyState])

  const handleRepath = useCallback((nodeId: string) => {
    if (readyState === WebSocket.OPEN) {
      sendMessage({
        type: 'repath',
        node_id: nodeId
      })
    }
  }, [sendMessage, readyState])

  const handleUpdateModels = useCallback((models: string[]) => {
    if (readyState === WebSocket.OPEN) {
      sendMessage({
        type: 'update_models',
        models
      })
    }
    setActiveModels(new Set(models))
  }, [sendMessage, readyState])

  const [optimizeLayoutFn, setOptimizeLayoutFn] = useState<(() => void) | null>(null)

  const handleOptimizeLayout = useCallback(() => {
    if (optimizeLayoutFn) {
      optimizeLayoutFn()
    }
  }, [optimizeLayoutFn])

  const handleOptimizeLayoutRegister = useCallback((fn: () => void) => {
    setOptimizeLayoutFn(() => fn)
  }, [])

  if (!isLoaded) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-lg">Loading auth...</div>
      </div>
    )
  }

  if (!isSignedIn) {
    return <SignInPage />
  }

  if (!clerkToken) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-lg">Preparing your session...</div>
      </div>
    )
  }

  // Show loading state only for initial load
  if (isInitialLoading || !rootNode) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-lg">Loading conversation...</div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen relative">
      <main className="w-full h-full">
        <ReactFlowProvider>
          <FlowChart
            rootNode={rootNode}
            allNodes={allNodes}
            onSelectResponse={handleSelectResponse}
            onRepath={handleRepath}
            onSendMessage={handleSendMessage}
            isLoading={isMessageStreaming}
            readyState={readyState}
            sessionId={sessionId}
            onOptimizeLayout={handleOptimizeLayoutRegister}
          >
            {isMessageStreaming && (
              <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-white/90 dark:bg-gray-800/90 px-4 py-2 rounded-full shadow-md z-50">
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                  <span className="text-sm">Sending message...</span>
                </div>
              </div>
            )}
            <Panel position="top-left" className="p-2">
              <div className="flex items-center space-x-2 opacity-50 hover:opacity-100 transition-opacity">
                <img src={graphchatLogo} alt="graphchat" className="h-6 w-6" />
                <span className="text-sm font-medium">graphchat</span>
              </div>
            </Panel>

            <div className="fixed top-4 right-4 z-50 flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleOptimizeLayout}
              >
                <LayoutGrid className="h-[1.2rem] w-[1.2rem]" />
              </Button>
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                  >
                    <Settings className="h-[1.2rem] w-[1.2rem]" />
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[800px] sm:max-w-[800px]">
                  <div className="grid grid-cols-2 gap-6 h-full">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Models</h3>
                      <ModelList
                        models={models}
                        activeModels={activeModels}
                        onUpdateModels={handleUpdateModels}
                      />
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Sessions</h3>
                      <SessionManager />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </FlowChart>
        </ReactFlowProvider>
      </main>
    </div>
  );
}

export default App
